/**
 * @file BloodPressureListView.jsx
 * @author Daniel Aguirre
 * @description Dashboard Detail Management View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React from 'react';
import PropTypes from 'prop-types';

// Helpers
import { useParams } from 'react-router';
import {
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  getSearchApiKeys,
  getSelectFilter,
  getSorterString,
  PAGE_LIMIT,
  setBadgeVariation,
  translateBooleanYesNo
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function BloodPressureListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = React.useState(totalItemsParams);
  const [dataFilter, setDataFilter] = React.useState(null);
  const [columns, setColumns] = React.useState([]);
  const [filter, setFilter] = React.useState(null);
  const [sorter, setSorter] = React.useState(null);

  const { getBloodPressureListPlus } = dashboardStaffActions;

  const columnAlert = {
    title: translate('dashboardStaff.alert'),
    dataIndex: 'badgeAlert',
    filter: {
      listName: 'lista_alerta_tension_alta',
      searchName: 'filterAlertaTensionAlta',
      type: 'select'
    },
    sorter: {
      compare: sortStringPlus('alerta_tension_alta'),
      multiple: 7
    },
    ...getSelectFilter(
      {
        filter: {
          listName: 'lista_alerta_tension_alta',
          searchName: 'filterAlertaTensionAlta'
        },
        dataIndex: 'alerta_tension_alta'
      },
      dataFilter
    ),
    align: 'center',
    ellipsis: true
  };

  const additionalColumns = [
    {
      title: translate('dashboardStaff.diabetesDiagnostic'),
      dataIndex: 'diagnostico_diabetes',
      filter: {
        listName: 'lista_diagnostico_diabetes',
        searchName: 'filterDiagnosticoDiabetes',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('diagnostico_diabetes'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_diagnostico_diabetes',
            searchName: 'filterDiagnosticoDiabetes'
          },
          dataIndex: 'diagnostico_diabetes'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.hipertensionDiagnostic'),
      dataIndex: 'diagnostico_hipertension',
      filter: {
        listName: 'lista_diagnostico_hipertension',
        searchName: 'filterDiagnosticoHipertension',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('diagnostico_hipertension'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_diagnostico_hipertension',
            searchName: 'filterDiagnosticoHipertension'
          },
          dataIndex: 'diagnostico_hipertension'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.glomerularDiagnostic'),
      dataIndex: 'diagnostico_enfermedad_glomerular',
      filter: {
        listName: 'lista_diagnostico_enfermedad_glomerular',
        searchName: 'filterDiagnosticoEnfermedadGlomerular',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('diagnostico_enfermedad_glomerular'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_diagnostico_enfermedad_glomerular',
            searchName: 'filterDiagnosticoEnfermedadGlomerular'
          },
          dataIndex: 'diagnostico_enfermedad_glomerular'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getAlert = (hasAlert) => {
    const bgColor = hasAlert === 'No Alerta' ? 'disabled' : 'error';
    const className = `m-auto rounded-full bg-${bgColor}`;
    return <div style={{ width: 24, height: 12 }} className={className}></div>;
  };

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        idClinic: clinicId,
        filterRegistroRiesgoSeleccionado: classification,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getBloodPressureListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        setTotalItems(resp.data?.totalRegistros);
        const newRowData = resp.data?.listaDetalles?.map((item) => {
          const diabetesDiagnostic = translateBooleanYesNo(
            item.diagnostico_diabetes,
            translate
          );
          const hipertensionDiagnostic = translateBooleanYesNo(
            item.diagnostico_hipertension,
            translate
          );
          const glomerularDiagnostic = translateBooleanYesNo(
            item.diagnostico_enfermedad_glomerular,
            translate
          );
          const bloodPressureBadge = getAlert(item.alerta_tension_alta);

          return {
            badgeAlert: bloodPressureBadge,
            ...item,
            diagnostico_diabetes: diabetesDiagnostic,
            diagnostico_hipertension: hipertensionDiagnostic,
            diagnostico_enfermedad_glomerular: glomerularDiagnostic
          };
        });
        setRowData(setBadgeVariation(newRowData ?? []));
        setDataFilter(resp.data);
      }
    });
  };

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [columnAlert, ...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  React.useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  React.useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  return (
    <DashboardDetailList
      columns={[columnAlert, ...columns]}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
BloodPressureListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getBloodPressureListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default BloodPressureListView;
